// Enums
export enum BankType {
  TELEBIRR = 'telebirr',
  AMOLE = 'amole',
  CBE = 'cbe',
  CBEBIRR = 'cbebirr',
  ABYSSINIA = 'abyssinia',
  AWASH = 'awash',
  CRYPTO = 'crypto',
  BINANCE_PAY = 'binance_pay',
  OTHER = 'other',
}

// User Types
export interface User {
  id: string;
  username: string;
  phoneNumber?: string;
  telegramUsername?: string;
  telegramId?: string;
  telegramProfilePictures?: string[];
  referralCode: string;
  wallet: number;
  createdAt: string;
  lastUsernameChange?: string;
  status?: 'active' | 'inactive'; // Made optional since not in API response
}

export interface UserGameStats {
  gamesPlayed: number;
  gamesWon: number;
  isDeposit: boolean;
  consecutiveWins: number;
  lastWinDate?: string;
  gameStats?: {
    [stakeAmount: string]: {
      played: number;
      won: number;
      totalStake: number;
      totalWinnings: number;
    };
  };
}

export interface CompleteUser extends User, UserGameStats {
  lastActivity?: string;
  status?: 'active' | 'inactive'; // Made optional since not in API response
}

export interface UserTableColumn {
  id: string;
  username: string;
  phoneNumber?: string;
  telegramUsername?: string;
  telegramId?: string;
  wallet: number;
  gamesPlayed: number;
  gamesWon: number;
  referralCode: string;
  createdAt: string;
  lastActivity?: string;
  status: 'active' | 'inactive';
}

export interface UserFilters {
  search?: string;
  status?: 'active' | 'inactive';
  hasDeposit?: boolean;
  minWallet?: number;
  maxWallet?: number;
  dateRange?: {
    start: string;
    end: string;
  };
  sortBy?: 'username' | 'wallet' | 'createdAt' | 'gamesPlayed';
  sortOrder?: 'asc' | 'desc';
}

export interface GetUsersResponse {
  users: CompleteUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  statistics: {
    totalUsers: number;
    activeUsers: number;
    totalWalletBalance: number;
  };
  filters: {
    search: string;
    sortBy: string;
    sortOrder: number;
  };
}


export interface WalletUpdateRequest {
  phoneNumber: string;
  amount: number;
}

export interface UserGrowthData {
  date: string;
  newUsers: number;
  totalUsers: number;
}

export interface TopPlayer {
  userId: string;
  username: string;
  telegramUsername?: string;
  totalWinnings: number;
  gamesWon: number;
  winRate: number;
}

export interface UserActivityData {
  hour: number;
  day: number;
  activeUsers: number;
}

export interface ReferralStats {
  totalReferrals: number;
  successfulReferrals: number;
  totalRewards: number;
  averageReward: number;
}

// Tournament Types
export type TournamentStatus = 'waiting' | 'countdown' | 'playing' | 'finished' | 'cancelled';
export type WinningPattern = 'standard' | 'fourCorners' | 'fullHouse' | 'diamond' | 'xPattern' | 'custom';

export interface Tournament {
  id: string;
  name: string;
  stake: number;
  maxPlayers: number;
  currentPlayers: number;
  players: string[];
  hostUserId: string;
  status: TournamentStatus;
  scheduledStartTime: string;
  actualStartTime?: string;
  winningPattern: WinningPattern;
  customPattern?: number[][];
  currentGame?: string;
  totalPrize: number;
  winners: string[];
  createdAt: string;
  updatedAt: string;
}

export interface TournamentForm {
  name: string;
  stake: number;
  maxPlayers: number;
  scheduledStartTime: string;
  hostUserId?: string;
  winningPattern: string;
  customPattern?: number[][];
}

export interface TournamentFilters {
  status?: TournamentStatus;
  search?: string;
  minStake?: number;
  maxStake?: number;
  minPlayers?: number;
  maxPlayers?: number;
  dateRange?: {
    start: string;
    end: string;
  };
  sortBy?: string;
  sortOrder?: number;
}

export interface GetTournamentsResponse {
  tournaments: Tournament[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  statistics: {
    totalTournaments: number;
    activeTournaments: number;
    totalPrizePool: number;
  };
  filters: {
    search: string;
    sortBy: string;
    sortOrder: number;
  };
}

export interface TournamentAnalytics {
  totalGames: number;
  averagePlayers: number;
  totalPrizePool: number;
  completionRate: number;
  popularPatterns: Array<{
    pattern: WinningPattern;
    count: number;
  }>;
  playerStats: Array<{
    userId: string;
    username: string;
    gamesPlayed: number;
    wins: number;
    totalWinnings: number;
  }>;
}

// Room Types
export type RoomStatus = 'waiting' | 'countdown' | 'playing' | 'finished';
export type RoomStake = 5 | 10 | 20 | 30 | 50 | 100 | 200 | 500 | 1000;

export interface RoomPlayer {
  id?: string;
  _id?: string;
  username: string;
  telegramUsername?: string;
  wallet?: number;
  telegramId?: string;
  status?: string;
  cards?: RoomCard[];
}

export interface RoomPlayerDetail {
  userId: string;
  username: string;
  telegramUsername: string | null;
  wallet: number;
  telegramId: string | null;
  status: string;
  cardId: number | null;
  hasPurchasedCard: boolean;
}

export interface RoomPlayersResponse {
  success: boolean;
  roomId: string;
  stake: number;
  status: string;
  totalPlayers: number;
  playersWithCards: number;
  players: RoomPlayerDetail[];
}

export interface RoomCard {
  id: string;
  card_id: number;
  status: 'available' | 'sold' | 'used';
  purchasedAt?: string;
}

export interface Room {
  _id: string;
  stake: RoomStake;
  players: RoomPlayer[];
  status: RoomStatus;
  countdown: number;
  currentGame?: string | null;
  createdAt: string;
  updatedAt?: string;
  startedAt?: string;
  endedAt?: string;
  deletedAt?: string;
  __v?: number;
}

export interface RoomForm {
  stake: RoomStake;
  winningPattern: string;
  cardCount: number;
}

export interface RoomFilters {
  status?: RoomStatus;
  stake?: RoomStake;
  sortBy?: string;
  sortOrder?: number;
}

export interface GetRoomsResponse {
  rooms: Room[];
  statistics: {
    totalRooms: number;
    activeRooms: number;
    waitingRooms: number;
    playingRooms: number;
  };
}

export interface RoomStatistics {
  totalPlayers: number;
  availableCards: number;
  soldCards: number;
  totalRevenue: number;
  averagePlayerBalance: number;
  topPlayers: Array<{
    userId: string;
    username: string;
    gamesWon: number;
    totalWinnings: number;
  }>;
}

export interface RoomPerformance {
  totalGames: number;
  averageGameDuration: number;
  totalRevenue: number;
  houseFee: number;
  totalPayouts: number;
  playerRetention: number;
  peakHours: Array<{
    hour: number;
    activePlayers: number;
  }>;
}

export interface RoomHistoryItem {
  gameId: string;
  startTime: string;
  endTime: string;
  playerCount: number;
  winner: {
    userId: string;
    username: string;
  };
  prize: number;
}

export interface RoomHistoryResponse {
  history: RoomHistoryItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Transaction Types
export interface Transaction {
  id: string;
  user: {
    id: string;
    username: string;
    phoneNumber: string;
  };
  recipient?: string;
  recipientUsername?: string;
  recipientTelegramId?: string;
  senderUsername?: string;
  senderTelegramId?: string;
  type: 'deposit' | 'withdraw' | 'purchase' | 'win' | 'house_fee' | 'refund' | 'transfer' | 'tournament_purchase' | 'tournament_refund' | 'tournament_win' | 'bonus' | 'ludo_purchase' | 'ludo_refund' | 'ludo_win' | 'ludo_house_fee' | 'agent_commission' | 'agent_commission_claim' | string;
  amount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
  lastUpdated: string;
}

export interface TransactionFilters {
  type?: string;
  username?: string;
  phoneNumber?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  minAmount?: number;
  maxAmount?: number;
  sortBy?: string;
  sortOrder?: number;
}

export interface GetTransactionsResponse {
  success: boolean;
  data: Transaction[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
}

export interface Invite {
  id: string;
  inviter: {
    id: string;
    username: string;
    phoneNumber: string;
  };
  inviteePhoneNumber: string;
  status: 'pending' | 'accepted' | 'rejected';
  rewardAmount: number;
  createdAt: string;
}

// Deposit Types
export interface Deposit {
  _id: string;
  id: string;
  phoneNumber: string;
  amount: number;
  agentPhone: string;
  reference: string;
  bankTransaction?: string;
  bankType: BankType;
  status: 'pending' | 'completed' | 'failed';
  timestamp: string;
  notes?: string;
  smsText?: string;
  cryptoAmount?: number;
  exchangeRate?: number;
  cryptoCurrency?: string;
  cryptoTxId?: string;
  paymentMethodType?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    username: string;
    telegramUsername?: string;
    phoneNumber: string;
  };
}

export interface DepositFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  agentPhone?: string;
  phoneNumber?: string;
  bankType?: string;
  minAmount?: number;
  maxAmount?: number;
  reference?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DepositStatistics {
  totalAmount: number;
  averageAmount: number;
  totalCount: number;
  statusBreakdown: {
    completed: {
      count: number;
      totalAmount: number;
    };
    pending: {
      count: number;
      totalAmount: number;
    };
    failed: {
      count: number;
      totalAmount: number;
    };
  };
}

export interface GetDepositsResponse {
  success: boolean;
  data: Deposit[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  filters: {
    status?: string;
    startDate?: string;
    endDate?: string;
    bankType?: string;
    sortBy?: string;
    sortOrder?: string;
  };
  statistics: DepositStatistics;
}

// Withdrawal Types
export interface Withdrawal {
  id: string;
  user: {
    id: string;
    username: string;
    telegramUsername?: string;
    telegramId?: string;
  };
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  bankType: string;
  phoneNumber: string;
  accountNumber: string;
  description: string;
  adminNote: string;
  processedBy?: {
    id: string;
    username: string;
  };
  createdAt: string;
  processedAt?: string;
}

export interface WithdrawalFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  phoneNumber?: string;
  bankType?: string;
  minAmount?: number;
  maxAmount?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface WithdrawalStatistics {
  totalAmount: number;
  averageAmount: number;
  totalCount: number;
  statusBreakdown: {
    pending: {
      count: number;
      totalAmount: number;
    };
    approved: {
      count: number;
      totalAmount: number;
    };
    rejected: {
      count: number;
      totalAmount: number;
    };
    cancelled: {
      count: number;
      totalAmount: number;
    };
  };
}

export interface GetWithdrawalsResponse {
  success: boolean;
  data: Withdrawal[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  filters: {
    status?: string;
    startDate?: string;
    endDate?: string;
    bankType?: string;
    sortBy?: string;
    sortOrder?: string;
  };
  statistics: WithdrawalStatistics;
}

// Dashboard Statistics
export interface DashboardReport {
  success: boolean;
  period: string;
  generatedAt: string;
  admin: {
    id: string;
    username: string;
    phoneNumber: string;
    telegramUsername: string;
    telegramId: string;
    wallet: number;
    isAdmin: boolean;
    createdAt: string;
  };
  overview: {
    totalUsers: number;
    totalDeposits: number;
    totalWithdrawals: number;
    netFlow: number;
  };
  today: {
    newUsers: number;
    deposits: {
      count: number;
      total: number;
    };
    withdrawals: {
      count: number;
      total: number;
    };
  };
  statistics: {
    users: {
      totalUsers: number;
      activeUsers: number;
      blockedUsers: number;
      newUsers?: number;
      totalWalletBalance: number;
      averageWalletBalance: number;
    };
    tournaments: {
      totalTournaments: number;
      totalRevenue: number;
      totalHouseFee: number;
      totalPlayers: number;
      averageStake: number;
      patternDistribution: {
        [key: string]: number;
      };
      statusDistribution: {
        [key: string]: number;
      };
      averagePlayersPerTournament: number;
    };
    rooms: {
      totalRooms: number;
      activeRooms: number;
      finishedRooms: number;
      totalPlayers: number;
      averagePlayersPerRoom: number;
    };
    deposits: {
      totalAmount: number;
      averageAmount: number;
      totalCount: number;
      statusBreakdown: {
        completed: {
          count: number;
          totalAmount: number;
        };
        pending: {
          count: number;
          totalAmount: number;
        };
        failed: {
          count: number;
          totalAmount: number;
        };
      };
    };
    withdrawals: {
      totalAmount: number;
      averageAmount: number | null;
      totalCount: number;
      statusBreakdown: {
        [key: string]: {
          count: number;
          totalAmount: number;
        };
      };
    };
    transactions: {
      stats: Array<{
        _id: string;
        count: number;
        totalAmount: number;
      }>;
      totalTransactions: number;
      totalAmount: number;
    };
  };
  recentActivities: {
    deposits: Array<{
      id: string;
      reference: string;
      amount: number;
      status: string;
      phoneNumber: string;
      bankType: string;
      user: {
        username: string;
        phoneNumber: string;
      } | null;
      createdAt: string;
    }>;
    withdrawals: Array<{
      id: string;
      amount: number;
      status: string;
      user: {
        username: string;
        phoneNumber: string;
      };
      createdAt: string;
    }>;
    transactions: Array<{
      id: string;
      type: string;
      amount: number;
      balanceAfter: number;
      description: string;
      user: {
        username: string;
        phoneNumber: string;
      };
      createdAt: string;
    }>;
  };
  charts: {
    userGrowth: {
      total: number;
      active: number;
      blocked: number;
    };
    financialOverview: {
      deposits: number;
      withdrawals: number;
      netFlow: number;
    };
    tournamentMetrics: {
      total: number;
      revenue: number;
      houseFee: number;
      averageStake: number;
    };
    roomMetrics: {
      total: number;
      active: number;
      finished: number;
      averagePlayers: number;
    };
  };
  // Additional properties used in DashboardPage
  reportPeriod?: {
    period: string;
    startDate: string;
    endDate: string;
  };
  transactionStats?: {
    byType: {
      deposit?: {
        totalAmount: number;
        count: number;
      };
      withdrawal?: {
        totalAmount: number;
        count: number;
      };
    };
    mostActiveUsers?: Array<{
      userId: string;
      username: string;
      telegramUsername?: string;
      transactionCount: number;
      totalAmount: number;
    }>;
  };
  userStats?: {
    totalUsers: number;
    newUsers: number;
    activeUsers?: number;
    blockedUsers?: number;
    topUsersByBalance?: Array<{
      userId: string;
      username: string;
      telegramUsername?: string;
      wallet: number;
    }>;
  };
  financialStats?: {
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    deposits?: {
      completed?: {
        count: number;
        totalAmount: number;
      };
      pending?: {
        count: number;
        totalAmount: number;
      };
      failed?: {
        count: number;
        totalAmount: number;
      };
    };
    withdrawals?: {
      pending?: {
        count: number;
        totalAmount: number;
      };
      approved?: {
        count: number;
        totalAmount: number;
      };
      rejected?: {
        count: number;
        totalAmount: number;
      };
      completed?: {
        count: number;
        totalAmount: number;
      };
    };
  };
  houseFeeIncome?: {
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
    transactionCount: number;
    breakdown: {
      [key: string]: {
        count: number;
        total: number;
      };
    };
    expenseBreakdown: {
      [key: string]: {
        count: number;
        total: number;
      };
    };
  };
  gameStats?: {
    activeRooms: number;
    totalGames: number;
    soldCards: number;
  };
  commissionStats?: {
    totalCommissionPaid: number;
    totalCommissionTransactions: number;
    topEarners?: Array<{
      _id: string;
      username: string;
      telegramUsername?: string;
      phoneNumber?: string;
      commissionCount?: number;
      totalCommission: number;
    }>;
  };
  referralStats?: {
    byStatus?: {
      accepted?: {
        count: number;
      };
    };
  };
}

export interface DashboardStats {
  totalUsers: number;
  totalDeposits: number;
  totalWithdrawals: number;
  activeTournaments: number;
  activeRooms: number;
  todayNewUsers: number;
  todayDeposits: number;
  todayWithdrawals: number;
}

// Analytics Types
export interface RevenueAnalytics {
  daily: Array<{ date: string; revenue: number }>;
  weekly: Array<{ week: string; revenue: number }>;
  monthly: Array<{ month: string; revenue: number }>;
}

export interface GameAnalytics {
  totalGames: number;
  gamesByStake: Array<{ stake: number; count: number }>;
  averageGameDuration: number;
  winRatesByStake: Array<{ stake: number; winRate: number }>;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  user: any;
  token: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Auth Types
export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: AdminData | null;
  loading: boolean;
}

// Filter Types
export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface TransactionFilter {
  type?: string;
  status?: string;
  dateRange?: DateRange;
}

export interface DepositFilter {
  status?: string;
  bankType?: string;
  dateRange?: DateRange;
}

export interface WithdrawalFilter {
  status?: string;
  bankType?: string;
  dateRange?: DateRange;
}

// Winning Pattern Management Types
export interface WinningPatternData {
  id?: string;
  name: string;
  description: string;
  patternType: 'builtin' | 'custom';
  builtinType?: 'standard' | 'fourCorners' | 'fullHouse' | 'diamond' | 'xPattern';
  customPattern?: number[][];
  visualPattern?: string;
  isActive?: boolean;
  createdBy?: {
    _id: string;
    username: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface WinningPatternForm {
  name: string;
  description: string;
  patternType: 'builtin' | 'custom';
  builtinType?: 'standard' | 'fourCorners' | 'fullHouse' | 'diamond' | 'xPattern';
  customPattern?: number[][];
  visualPattern?: string;
}

export interface GetWinningPatternsResponse {
  success: boolean;
  data?: WinningPatternData[];
  patterns?: WinningPatternData[];
  pattern?: WinningPatternData;
  pagination?: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
}

export interface WinningPatternFilters {
  patternType?: 'builtin' | 'custom';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Settings Management Types
export interface SettingsData {
  inviterCommissionPercent: number;
  serviceFeePercent: number;
  tournamentServiceFeePercent: number;
  minimumCommissionAmount: number;
  bonusRegistrationAmount: number;
  agentDepositCommissionPercent?: number;
  agentMinimumClaimAmount?: number;
  depositSelfBonusEnabled?: boolean;
  depositSelfBonusAmount?: number;
  depositSelfBonusPercent?: number;
  depositSelfBonusFirstOnly?: boolean;
  depositInviterBonusEnabled?: boolean;
  depositInviterBonusAmount?: number;
  depositInviterBonusPercent?: number;
  depositInviterBonusFirstOnly?: boolean;
  drawIntervalSeconds: number;
  countdownSeconds: number;
  minimumPlayers: number;
  isActive: boolean;
  botEnabled: boolean;
  maxBotsPerRoom?: number;
  houseAccountPhone?: string;
  spinHouseLimitDaily?: number;
  bonusSystemEnabled?: boolean;
  dailyActiveUserBonusAmount?: number;
  dailyActiveUserMinimumGames?: number;
  topWinnerBonusAmount?: number;
  topWinnerBonusCount?: number;
  topWinnerBonusPeriod?: string;
  topWinnerBonusMinimumWinnings?: number;
  promoCarouselEnabled?: boolean;
  midnightLuckyEnabled?: boolean;
  midnightLuckyWinnerCount?: number;
  midnightLuckyAmount?: number;
  midnightLuckyMinGames?: number;
  drawMode?: 'fair' | 'bot_advantage' | 'block_human';
  flashDepositPromoEnabled?: boolean;
  flashDepositMinHoursBetween?: number;
  flashDepositDefaultDurationMinutes?: number;
  updatedAt?: string;
}

export interface SettingsForm {
  inviterCommissionPercent: number;
  serviceFeePercent: number;
  tournamentServiceFeePercent: number;
  minimumCommissionAmount: number;
  bonusRegistrationAmount: number;
  agentDepositCommissionPercent: number;
  agentMinimumClaimAmount: number;
  depositSelfBonusEnabled: boolean;
  depositSelfBonusAmount: number;
  depositSelfBonusPercent: number;
  depositSelfBonusFirstOnly: boolean;
  depositInviterBonusEnabled: boolean;
  depositInviterBonusAmount: number;
  depositInviterBonusPercent: number;
  depositInviterBonusFirstOnly: boolean;
  drawIntervalSeconds: number;
  countdownSeconds: number;
  minimumPlayers: number;
  isActive: boolean;
  botEnabled: boolean;
  maxBotsPerRoom: number;
  houseAccountPhone: string;
  spinHouseLimitDaily: number;
  bonusSystemEnabled: boolean;
  dailyActiveUserBonusAmount: number;
  dailyActiveUserMinimumGames: number;
  topWinnerBonusAmount: number;
  topWinnerBonusCount: number;
  topWinnerBonusPeriod: string;
  topWinnerBonusMinimumWinnings: number;
  promoCarouselEnabled: boolean;
  midnightLuckyEnabled: boolean;
  midnightLuckyWinnerCount: number;
  midnightLuckyAmount: number;
  midnightLuckyMinGames: number;
  drawMode: 'fair' | 'bot_advantage' | 'block_human';
  flashDepositPromoEnabled?: boolean;
  flashDepositMinHoursBetween?: number;
  flashDepositDefaultDurationMinutes?: number;
}

export interface GetSettingsResponse {
  success: boolean;
  settings: SettingsData;
} 

// Payment Method Management Types
export type JustVerifyProvider = 'none' | 'cbe' | 'boa' | 'telebirr' | 'cbebirr';

export interface PaymentMethodData {
  id: string;
  name: string;
  accountNumber: string;
  verifyAccountName?: string;
  justVerifyProvider?: JustVerifyProvider;
  description: string;
  type: 'bank' | 'mobile_money' | 'crypto' | 'other';
  icon: string;
  sortOrder: number;
  isActive: boolean;
  exchangeRate?: number;
  cryptoCurrency?: string;
  network?: string;
  binanceIdType?: 'pay_id' | 'binance_id' | 'email' | 'phone' | 'other';
  createdAt?: string;
  updatedAt?: string;
}

export interface PaymentMethodForm {
  name: string;
  accountNumber: string;
  verifyAccountName?: string;
  justVerifyProvider?: JustVerifyProvider;
  description: string;
  type: 'bank' | 'mobile_money' | 'crypto' | 'other';
  icon: string;
  sortOrder: number;
  exchangeRate?: number;
  cryptoCurrency?: string;
  network?: string;
  binanceIdType?: 'pay_id' | 'binance_id' | 'email' | 'phone' | 'other';
}

export interface PaymentMethodUpdateForm extends PaymentMethodForm {
  isActive?: boolean;
}

export interface GetPaymentMethodsResponse {
  success: boolean;
  data?: PaymentMethodData[];
  methods?: PaymentMethodData[];
  method?: PaymentMethodData;
  pagination?: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
}

export interface PaymentMethodFilters {
  type?: 'bank' | 'mobile_money' | 'crypto' | 'other';
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaymentMethodReorderRequest {
  order: Array<{
    id: string;
    sortOrder: number;
  }>;
}

// Admin Management Types
export interface AdminData {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  role: 'owner' | 'admin' | 'superadmin';
  lastLogin?: string;
  isActive: boolean;
  otp?: {
    code: string;
    expiresAt: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminForm {
  fullName: string;
  email: string;
  phoneNumber: string;
  role: 'owner' | 'admin' | 'superadmin';
}

export interface AdminUpdateForm {
  fullName?: string;
  phoneNumber?: string;
  role?: 'owner' | 'admin' | 'superadmin';
}

export interface GetAdminsResponse {
  success: boolean;
  data?: AdminData[];
  admins?: AdminData[];
  admin?: AdminData;
  pagination?: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
}

export interface AdminFilters {
  role?: 'owner' | 'admin' | 'superadmin';
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface RequestOtpRequest {
  email: string;
}

export interface VerifyOtpRequest {
  email: string;
  otp: string;
}

export interface VerifyOtpResponse {
  success: boolean;
  token?: string;
  admin?: AdminData;
  message?: string;
}

// Media Management Types
export interface MediaData {
  id: string;
  fileUrl: string;
  isVideo: boolean;
  type: 'creation' | 'starting' | 'ending';
  createdAt: string;
  updatedAt?: string;
}

export interface MediaForm {
  file: File;
  isVideo: boolean;
  type: 'creation' | 'starting' | 'ending';
}

export interface MediaUpdateForm {
  type?: 'creation' | 'starting' | 'ending';
  isVideo?: boolean;
}

export interface MediaFilters {
  type?: 'creation' | 'starting' | 'ending';
  isVideo?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetMediaResponse {
  success: boolean;
  media: MediaData[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  filters: {
    type?: string;
    isVideo?: string;
    search?: string;
  };
}

export interface MediaStatistics {
  totalMedia: number;
  totalVideos: number;
  totalImages: number;
  recentUploads: number;
  mediaByType: Array<{
    _id: string;
    count: number;
  }>;
}

// Agent Request Types
export interface AgentRequestUser {
  id: string;
  username: string;
  phoneNumber: string;
  telegramId?: string;
  telegramUsername?: string;
  wallet: number;
  bonusBalance: number;
  isAgent: boolean;
  createdAt: string;
}

export interface AgentRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  city: string;
  phoneNumber: string;
  telegramPhoneNumber: string;
  hasExperienceWithVirtualGames: boolean;
  hasCommunity: boolean;
  requestMessage: string;
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
  user: AgentRequestUser;
  processedBy: string | null;
}

export interface AgentRequestFilters {
  status?: 'pending' | 'approved' | 'rejected';
  city?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AgentRequestsResponse {
  success: boolean;
  data: {
    requests: AgentRequest[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    counts: {
      pending: number;
      approved: number;
      rejected: number;
    };
  };
}

export interface ApproveAgentRequestResponse {
  success: boolean;
  message: string;
  data?: AgentRequest;
}

export interface RejectAgentRequestResponse {
  success: boolean;
  message: string;
  data?: AgentRequest;
}

export interface GetMediaStatsResponse {
  success: boolean;
  statistics: MediaStatistics;
}

export interface GiftBonusPreview {
  amount: number;
  eligibleUsers: number;
  withTelegram: number;
  totalCost: number;
  defaultAmount: number;
  cooldownRemainingMs: number;
  lastCampaign: {
    id: string;
    batchId: string;
    at: string;
    amount: number;
    usersCredited: number;
    error: string | null;
    telegramStats: unknown;
    telegramPending: boolean;
  } | null;
}